// src/components/ResponsiveImage.js
//
// P3-4 — Responsive <img> wrapper. CF Image Resizing varsa srcset üretir,
// yoksa orijinal URL ile devam eder (graceful fallback).
//
// Usage:
//   <ResponsiveImage
//     src={user.photoURL}
//     widths={[160, 320, 640]}
//     sizes="(max-width: 640px) 320px, 640px"
//     alt={user.username}
//     className="avatar"
//     fallback="/avatars/default.svg"
//     loading="lazy"
//   />
//
// Davranış:
//   - imageVariantsEnabled() true → src = en kuçuk variant, srcset = tum widths
//   - false → src = orijinal URL, srcset gönderilmez
//   - onError → fallback'e geç (default avatar vs.)
//   - loading="lazy" varsayılan (browser-native lazy load)
import React, { useState } from "react";
import { buildImageVariant, buildImageSrcSet } from "../utils/r2Url";

interface ResponsiveImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  src?: string | null;
  widths?: number[];
  sizes?: string;
  fit?: string;
  format?: string;
  fallback?: string;
  alt?: string;
  loading?: "lazy" | "eager";
  className?: string;
  onLoad?: React.ReactEventHandler<HTMLImageElement>;
  onError?: React.ReactEventHandler<HTMLImageElement>;
}

function ResponsiveImage({
  src,
  widths,
  sizes,
  fit = "cover",
  format = "auto",
  fallback = "",
  alt = "",
  loading = "lazy",
  className,
  onLoad,
  onError,
  ...rest
}: ResponsiveImageProps) {
  const [hadError, setHadError] = useState(false);

  // Origin yoksa fallback'e doğrudan geç.
  const effectiveOrigin = src || fallback || null;
  if (!effectiveOrigin) return null;

  // Variant URL'leri (env yoksa orijinal döner)
  const widthsList = Array.isArray(widths) && widths.length > 0
    ? widths
    : [320];
  // En küçük variant ana src; srcset tüm liste.
  const minW = Math.min(...widthsList.filter((n) => Number.isFinite(n) && n > 0));
  const baseVariant =
    buildImageVariant(effectiveOrigin, { width: minW, fit, format }) ||
    effectiveOrigin;
  const srcSet = buildImageSrcSet(effectiveOrigin, widthsList, { fit, format });

  const finalSrc = hadError && fallback ? fallback : baseVariant;
  const finalSrcSet = hadError ? "" : srcSet;

  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (!hadError && fallback) {
      setHadError(true);
    }
    if (onError) onError(e);
  };

  return (
    <img
      src={finalSrc}
      srcSet={finalSrcSet || undefined}
      sizes={finalSrcSet ? sizes : undefined}
      alt={alt}
      className={className}
      loading={loading}
      onLoad={onLoad}
      onError={handleError}
      {...rest}
    />
  );
}

export default React.memo(ResponsiveImage);
